import { createContext, ReactNode } from "react"

// project-imports
import config from "config"
import useLocalStorage from "hooks/useLocalStorage"

// types
import { CustomizationProps, FontFamily, MenuOrientation, PresetColor, ThemeDirection, ThemeMode } from "types/config"
import { AppLocale } from "@/config/locales"

// initial state
const initialState: CustomizationProps = {
    ...config,
    onChangeContainer: () => {},
    onChangeLocalization: (_lang: AppLocale) => {},
    onChangeMode: (_mode: ThemeMode) => {},
    onChangePresetColor: (_theme: PresetColor) => {},
    onChangeDirection: (_direction: ThemeDirection) => {},
    onChangeMiniDrawer: (_miniDrawer: boolean) => {},
    onChangeMenuOrientation: (_menuOrientation: MenuOrientation) => {},
    onChangeMenuCaption: () => {},
    onChangeFontFamily: (_fontFamily: FontFamily) => {},
    onChangeContrast: () => {},
}

// ==============================|| CONFIG CONTEXT & PROVIDER ||============================== //

const ConfigContext = createContext(initialState)

type ConfigProviderProps = {
    children: ReactNode
}

function ConfigProvider({ children }: ConfigProviderProps) {
    const [config, setConfig] = useLocalStorage("app-config", initialState)

    const onChangeContainer = () => {
        setConfig({
            ...config,
            container: !config.container,
        })
    }

    const onChangeLocalization = (lang: AppLocale) => {
        setConfig({
            ...config,
            i18n: lang,
        })
    }

    const onChangeMode = (mode: ThemeMode) => {
        setConfig({
            ...config,
            mode,
        })
    }

    const onChangePresetColor = (theme: PresetColor) => {
        setConfig({
            ...config,
            presetColor: theme,
        })
    }

    const onChangeDirection = (direction: ThemeDirection) => {
        setConfig({
            ...config,
            themeDirection: direction,
        })
    }

    const onChangeMiniDrawer = (miniDrawer: boolean) => {
        setConfig({
            ...config,
            miniDrawer,
        })
    }

    const onChangeContrast = () => {
        setConfig({
            ...config,
            themeContrast: !config.themeContrast,
        })
    }

    const onChangeMenuCaption = () => {
        setConfig({
            ...config,
            menuCaption: !config.menuCaption,
        })
    }

    const onChangeMenuOrientation = (layout: MenuOrientation) => {
        setConfig({
            ...config,
            menuOrientation: layout,
        })
    }

    const onChangeFontFamily = (fontFamily: FontFamily) => {
        setConfig({
            ...config,
            fontFamily,
        })
    }

    return (
        <ConfigContext.Provider
            value={{
                ...config,
                onChangeContainer,
                onChangeLocalization,
                onChangeMode,
                onChangePresetColor,
                onChangeDirection,
                onChangeMiniDrawer,
                onChangeMenuOrientation,
                onChangeMenuCaption,
                onChangeFontFamily,
                onChangeContrast,
            }}
        >
            {children}
        </ConfigContext.Provider>
    )
}

export { ConfigProvider, ConfigContext }
